import { memo, useMemo } from 'react';
import { useExperienceMode } from '@/contexts/ExperienceModeContext';

/**
 * Lightweight overlay for Alternate Mode - optimized for performance
 * Only renders minimal visual effects when in alternate mode
 */
const AlternateModeOverlay = memo(() => {
  const { isAlternate, getModeIntensity } = useExperienceMode();

  const intensity = getModeIntensity();

  const overlayStyle = useMemo(() => ({
    background: isAlternate 
      ? `radial-gradient(ellipse at center, transparent 0%, hsl(var(--background) / ${0.08 + intensity * 0.12}) 100%)`
      : 'transparent',
    opacity: isAlternate ? 1 : 0,
  }), [isAlternate, intensity]);

  if (!isAlternate) return null;

  return (
    <>
      {/* Subtle vignette overlay */}
      <div 
        className="fixed inset-0 pointer-events-none z-30 transition-opacity duration-1000"
        style={overlayStyle}
        aria-hidden="true"
      />

      {/* Static grain texture - CSS only, no JS updates */}
      <div 
        className="fixed inset-0 pointer-events-none z-35 opacity-[0.02] mix-blend-overlay"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
        }}
        aria-hidden="true"
      />

      {/* Edge vignette for depth */}
      <div 
        className="fixed inset-0 pointer-events-none z-30"
        style={{
          background: `radial-gradient(ellipse at center, transparent 50%, hsl(var(--background)) ${95 - intensity * 15}%)`,
          opacity: 0.4 + intensity * 0.2,
        }}
        aria-hidden="true"
      />
    </>
  );
});

AlternateModeOverlay.displayName = 'AlternateModeOverlay';

export default AlternateModeOverlay;